import {
    axiosWithTokenRefresh,
    axiosConfigs,
    API_ERRORS
} from 'tradera/utils/api';
import { toLocalizedUrl } from 'tradera/utils/url';
import { logger } from 'packages/logger';
import { webApiClient } from 'tradera/utils/http';
import type { GetItemDescriptionApiResponse } from 'tradera/state/services/types/webapi-browse-generated';
import type {
    MoreFromSellerApiRequest,
    MoreFromSellerApiResponse,
    MoreItemsForCombinedShippingApiResponse,
    MoreItemsForCombinedShippingRequest
} from 'tradera/state/services/types/webapi-discover-generated';

export const fetchDescription = (itemId: number, languageCodeIso2: string) => {
    return webApiClient.get<GetItemDescriptionApiResponse>(
        `/browse/item/${itemId}/description/?languageCodeIso2=${languageCodeIso2}`,
        {
            cancelTokenId: 'getItemDescriptionTranslated',
            ...axiosConfigs.authenticated
        }
    );
};

type ValidationResult = {
    code: string;
    title: string;
    body: string;
};

type PlaceBidApiResponse = {
    bid: {
        bidId: number;
        hasLostLeadingBid: boolean;
        isSuccessful: boolean;
        leadingBidAmount: number;
        leadingBidderMemberId: number;
        leadingMemberAlias: string;
        nextBidAmount: number;
        validationResult: ValidationResult;
        placedBid: number;
    };
    item: {
        itemId: number;
        bidCount: number | null;
        categoryId: number;
        endDate: string;
        itemType: string;
        openingBid: number | null;
        reservePriceReached: boolean;
        sellermemberId: number;
        startDate: string;
        leadingBid: number;
    };
    transactionTotal: number;
    potentialBidChange: number;
    isMaxBid: boolean;
    serverSecondsLeft: number;
};

/**
 * Places a bid for the given auction
 */
export const placeBidApi = async (
    itemId: number,
    amount: number,
    onLoggedOut: () => void,
    preferredLanguageCode: string,
    gaTrackingSessionId: string
) => {
    try {
        const response =
            await axiosWithTokenRefresh().post<PlaceBidApiResponse>(
                toLocalizedUrl(
                    `/item/place-bid/${itemId}`,
                    preferredLanguageCode
                ),
                { amount, gaTrackingSessionId },
                axiosConfigs.authenticated
            );
        const {
            data: {
                bid,
                item,
                transactionTotal,
                potentialBidChange,
                isMaxBid,
                serverSecondsLeft
            }
        } = response;

        const {
            bidId,
            hasLostLeadingBid,
            isSuccessful,
            leadingBidAmount,
            leadingBidderMemberId,
            leadingMemberAlias: leadingBidMemberAlias,
            nextBidAmount: nextValidBidAmount,
            validationResult,
            placedBid
        } = bid;

        const {
            bidCount,
            categoryId,
            endDate,
            itemType,
            openingBid,
            reservePriceReached,
            sellermemberId: sellerMemberId,
            startDate
        } = item;

        return {
            isSuccessful,
            validationResult,
            item: {
                bidCount,
                categoryId,
                endDate,
                itemId: item.itemId,
                itemType,
                openingBid,
                reservePriceReached,
                sellerMemberId,
                startDate,
                leadingBid: item.leadingBid
            },
            bidInfo: {
                bidCount,
                bidId,
                hasLostLeadingBid,
                isMaxBid,
                leadingBidAmount,
                leadingBidderMemberId,
                leadingBidMemberAlias,
                nextValidBidAmount,
                potentialBidChange,
                reservePriceReached,
                transactionTotal
            },
            myMaxBidAmount: placedBid,
            serverSecondsLeft
        };
    } catch (error) {
        if (error instanceof Error && error.message === API_ERRORS.LOGGED_OUT) {
            onLoggedOut();
            return;
        }

        logger(error);
    }
};

export const getItemsFromSeller = (
    params: MoreFromSellerApiRequest,
    preferredLanguageCode: string
) => {
    return webApiClient.get<MoreFromSellerApiResponse>(
        toLocalizedUrl('/discover/more-from-seller/', preferredLanguageCode),
        { params }
    );
};

export const getCombinedShippingUpsell = (
    params: MoreItemsForCombinedShippingRequest
) => {
    return webApiClient.get<MoreItemsForCombinedShippingApiResponse>(
        '/discover/web/more-items/for-combined-shipping',
        { params }
    );
};
